/**
 * BUG-1 workaround — FE-side naive-ISO converter for the handoff +
 * handoff-request endpoints.
 *
 * Backend bug (filed as BE-210 / FE issue #207): the
 * `item_custody_event.due_date` column is `TIMESTAMP WITHOUT TIME
 * ZONE` on Postgres. When the endpoint receives a tz-aware ISO
 * (`...Z`) and tries to compare to `datetime.utcnow()` (also naive),
 * Python throws `DataError: can't subtract offset-naive and
 * offset-aware datetimes` → 500.
 *
 * Until the server normalizes the input (PR coming on the BE side),
 * the FE pre-strips the timezone marker. The semantic stays identical
 * — backend reads it as UTC because that's the only convention
 * used anywhere else in the codebase, and `new Date(...).toISOString()`
 * was already emitting UTC. We just drop the trailing `Z` so the
 * comparison-to-tz-naive datetime succeeds.
 *
 *   '2026-05-26T23:00:00.000Z' → '2026-05-26T23:00:00'
 *
 * Examples:
 *   toNaiveIso(null)                            → null
 *   toNaiveIso(undefined)                       → null
 *   toNaiveIso('')                              → null
 *   toNaiveIso('2026-05-26T23:00:00.000Z')      → '2026-05-26T23:00:00'
 *   toNaiveIso('2026-05-26T23:00:00Z')          → '2026-05-26T23:00:00'
 *   toNaiveIso('2026-05-26T23:00:00')           → '2026-05-26T23:00:00'  (no-op)
 *   toNaiveIso('2026-05-26T23:00:00+05:30')     → '2026-05-26T17:30:00'  (UTC-normalized via Date)
 *
 * Once the backend ships its naive→aware coercion, this helper becomes
 * a no-op — delete it then.
 */
export function toNaiveIso(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Fast path: already tz-naive (no Z, no +HH:MM offset). Strip any
  // milliseconds to be safe but otherwise return as-is.
  const tzMatch = /(Z|[+-]\d{2}:?\d{2})$/.exec(trimmed);
  if (!tzMatch) {
    // Drop any millisecond fraction; backend's column is second-precision.
    return trimmed.replace(/\.\d+/, '');
  }

  // Round-trip through Date so non-UTC offsets get normalized to UTC,
  // then strip the trailing 'Z' + milliseconds. If the input was an
  // invalid date string, fall back to a raw strip of the suffix.
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) {
    return trimmed.replace(/(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/, '');
  }
  // toISOString always returns `...Z` form. Strip the millis + `Z`.
  return d.toISOString().replace(/\.\d+Z$/, '');
}
